// Flame Cell manufacturing routing, computed in cpq2.
//
// A faithful port of the NetSuite User Event script that used to do this
// (afterSubmit on serialized assembly items, gated on flame/spark cell type +
// custitem_cpq_validated). That script is being deactivated, so this is now the only
// thing that builds a Flame Cell routing.
//
// WHY THIS CAN BE A LITERAL PORT
// The script read the ITEM's custom fields. cpq2 does not need to re-derive any of
// them from answers, because the Flame Cells ITCR already computes exactly those
// fields -- custitemflamecell_die is {CELL_DIE2/INTERNAL_ID}, custitem_fco_flamecell_matl
// is {CELL_MATERIAL2/INTERNAL_ID}, and so on -- and buildAssembly has them in
// preview.item.fields before the routing is assembled. So this reads the same internal
// ids off the same fields, and the labor model below is copied value for value.
//
// WHAT IS DELIBERATELY NOT PORTED
//   record.load          cpq2 has the field values already; there is nothing to load.
//   routingExists(name)  the script deduped by routing NAME because it could re-fire on
//                        any later edit. cpq2 creates a routing exactly once per BOM:
//                        api/finish-assembly looks up getRoutingId(bomId) and only
//                        creates when there is none. Different mechanism, same outcome,
//                        and the BOM-scoped one is stricter.
//   the CREATE/EDIT and  cpq2 only ever runs this while building an assembly it is
//   item-type triggers   creating itself, so there is no trigger to gate.
// The TYPE and VALIDATED gates ARE ported: both are set by the ITCR so they pass by
// construction today, but checking them means a config that somehow lacks them yields
// no routing rather than a wrong one.

/** One routing step, in the shape buildAssembly's RoutingOp expects. */
export type ComputedRoutingOp = {
  opseq: string;
  name: string;
  setupTime: string;
  runRate: string;
  workCenterId: string;
  workCenter: string;
  costTemplateId: string;
  costTemplate: string;
  /** "SS" on every step except the first, per the original script. */
  connectionType?: string;
};

export type FlameCellRoutingResult =
  | { ok: true; operations: ComputedRoutingOp[]; subsidiaryId: string; locationId: string; detail: string }
  | { ok: false; reason: string };

// ================================ CONFIG ================================
// Copied from the script. Internal ids are NetSuite records, so they are data, not
// magic numbers -- changing one here changes which work center the shop floor sees.
const CONFIG = {
  TYPE_FIELD: "custitem_fco_flamecell_type", // 1 = Spark, 2 = Flame
  VALIDATED_FIELD: "custitem_cpq_validated",

  SUBSIDIARY_ID: "1", // Parent Company
  LOCATION_ID: "1", // Flameco Main - Building 1

  // '' => derive OD from the part number (F6D-32000-... => 32.0"). The script shipped
  // with this empty, so the part number is the OD source. custitem_flamecellod carries
  // the same value and is used as a fallback when the name does not parse.
  OD_FIELD: "",

  SETUP_TIME: "5",
  CONNECTION_TYPE: "SS", // every step EXCEPT the first

  WC_ROLL_PA: "1735", // 3000 - Cell Rolling - PA   (D die)
  WC_ROLL_CC: "1736", // 3500 - Cell Rolling - CC   (all other dies)
  WC_TIG: "10933", // 2500 - Tig Welding
  WC_SHIP: "116", // 6000 - Shipping

  CT_ROLL: "13",
  CT_TIG: "9", // MCT - Welding Lvl 1
  CT_SHIP: "11", // MCT - Shipping

  SEQ_ROLL: "10",
  NAME_ROLL: "Cell Rolling",
  SEQ_TIG: "20",
  NAME_TIG: "Tig Welding",
  SEQ_SHIP: "30",
  NAME_SHIP: "Inspection & Shipping",
} as const;

const FIELDS = {
  die: "custitemflamecell_die",
  matl: "custitem_fco_flamecell_matl",
  housing: "custitemflamecell_housingtype",
  insp: "custitem_flamecell_inspectiontype",
  plug: "custitemflamecell_coreplugtype",
  od: "custitem_flamecellod",
} as const;

// ===================== VALUE MAPS (internal id => model) ===============
const DIE: Record<string, string> = { "1": "B", "2": "B1", "3": "C", "4": "HE", "5": "HEZ", "6": "HD", "7": "D" };
const MATL: Record<string, string> = { "1": "Aluminum", "3": "304 SS", "4": "316 SS" };
const INSP_MIN: Record<string, number> = { "1": 5, "2": 10 }; // Wrap => 5, Pin => 10
const HOUSING_ADD: Record<string, number> = { "1": 30, "2": 15, "3": 0 }; // Housing / Sleeve / None
const WELDED_PLUGS: Record<string, boolean> = { "5": true, "6": true, "7": true };
const TIG_BASE = 3;
const PA_STEP_OD = 30;
const DEFAULT_INSP_MIN = 10;

// ===================== LABOR MODEL =====================================
// ss = [slope, intercept] for stainless; pa = [under PA_STEP_OD, at or over] per inch
// for aluminum. A die with no `pa` uses the ss coefficients even for aluminum, which is
// how the original behaves (`material === 'Aluminum' && p.pa`).
const ROLL: Record<string, { ss: [number, number]; pa?: [number, number] }> = {
  B: { ss: [6.544, -4.8223] },
  B1: { ss: [7.3973, -4.8871] },
  C: { ss: [4.8504, -5.0723] },
  D: { ss: [2.3115, -5.0626], pa: [1.1, 1.5] },
  HD: { ss: [2.3229, -5.2264], pa: [1.21, 1.65] },
  HE: { ss: [3.2291, -5.7373], pa: [1.452, 1.98] },
  // HEZ is rolled as HE (see modelDie).
};

/** HEZ shares HE's rolling coefficients. */
function modelDie(d: string): string {
  return d === "HEZ" ? "HE" : d;
}
function round2(x: number): number {
  return Math.round(x * 100) / 100;
}

/**
 * Material / die / OD parsed out of the part number, for when a structured field is
 * blank. F6D-32000-... => 316 SS, D, 32.0". The optional C matches the two-letter
 * FC/SC (Flame Cell / Spark Cell) prefix, e.g. SCAD-5175-..., FC4D-....
 */
export function nameTokens(itemid: string): { material: string; die: string; od: number } | null {
  if (!itemid) return null;
  const m = String(itemid)
    .toUpperCase()
    .match(/([FS])C?([A46])(B1|HEZ|HZ|HE|HD|B|C|D)-(\d+)/);
  if (!m) return null;
  const matMap: Record<string, string> = { A: "Aluminum", "4": "304 SS", "6": "316 SS" };
  const dieMap: Record<string, string> = {
    HZ: "HEZ",
    HEZ: "HEZ",
    HE: "HE",
    HD: "HD",
    B1: "B1",
    B: "B",
    C: "C",
    D: "D",
  };
  return { material: matMap[m[2]] ?? "", die: dieMap[m[3]] ?? "", od: parseInt(m[4], 10) / 1000 };
}

/** Inches from a display string: 1-1/2, 3/4, 12", unicode fractions. */
export function parseInches(str: unknown): number | null {
  if (str == null) return null;
  let t = String(str).replace(/["”“]/g, "").trim().replace(/–/g, "-");
  const uni: Record<string, number> = {
    "½": 0.5,
    "¼": 0.25,
    "¾": 0.75,
    "⅛": 0.125,
    "⅜": 0.375,
    "⅝": 0.625,
    "⅞": 0.875,
  };
  for (const k of Object.keys(uni)) {
    if (t.indexOf(k) >= 0) t = t.replace(k, (t.replace(k, "").length ? "+" : "") + uni[k]);
  }
  let m = t.match(/^(\d+)\s*-\s*(\d+)\s*\/\s*(\d+)$/);
  if (m) return parseInt(m[1], 10) + parseInt(m[2], 10) / parseInt(m[3], 10);
  m = t.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (m) return parseInt(m[1], 10) / parseInt(m[2], 10);
  m = t.match(/^(\d+)\s*\+\s*([\d.]+)$/);
  if (m) return parseFloat(m[1]) + parseFloat(m[2]);
  if (/^\d*\.?\d+$/.test(t)) return parseFloat(t);
  return null;
}

/** Names the shop uses to park a part. The script refuses to route these. */
export function isDontUse(itemid: string): boolean {
  return /^\s*(DON.?T USE|DO NOT USE|DUPLICATE)/i.test(String(itemid ?? ""));
}

/** Rolling run rate + which cell-rolling work center, from die / material / OD. */
export function computeRolling(
  dieCode: string,
  material: string,
  od: number | null
): { roll: number; rollWC: string } | { error: string } {
  if (!dieCode) return { error: "blank/unmapped Die" };
  if (!material) return { error: "blank/unmapped Material" };
  if (!(od != null && od > 0)) return { error: "missing/invalid OD" };
  const p = ROLL[modelDie(dieCode)];
  if (!p) return { error: "die not in labor model: " + dieCode };
  let roll: number;
  if (material === "Aluminum" && p.pa) roll = (od >= PA_STEP_OD ? p.pa[1] : p.pa[0]) * od;
  else roll = Math.max(0, p.ss[0] * od + p.ss[1]);
  return { roll: round2(roll), rollWC: dieCode === "D" ? CONFIG.WC_ROLL_PA : CONFIG.WC_ROLL_CC };
}

function step(
  seq: string,
  name: string,
  workCenterId: string,
  costTemplateId: string,
  runRate: number,
  first: boolean
): ComputedRoutingOp {
  return {
    opseq: seq,
    name,
    setupTime: CONFIG.SETUP_TIME,
    runRate: String(runRate),
    workCenterId,
    workCenter: "",
    costTemplateId,
    costTemplate: "",
    // The original sets connectiontype on every step except the lowest sequence.
    ...(first ? {} : { connectionType: CONFIG.CONNECTION_TYPE }),
  };
}

/**
 * Build the 3-step Flame Cell routing (Cell Rolling, Tig Welding, Inspection &
 * Shipping) from the item fields the ITCR just computed.
 *
 * Tig Welding is OMITTED when its rate would be 0 -- an unwelded core plug with no
 * housing or sleeve addition -- exactly as the script did.
 */
export function buildFlameCellRouting(args: {
  itemid: string;
  fields: Record<string, string>;
}): FlameCellRoutingResult {
  const { itemid, fields } = args;
  const f = (k: string) => String(fields[k] ?? "");

  // ---- GATE 1: flame/spark cell type ----
  const typeVal = f(CONFIG.TYPE_FIELD);
  if (typeVal !== "1" && typeVal !== "2") {
    return { ok: false, reason: `not a flame/spark cell (${CONFIG.TYPE_FIELD}=${JSON.stringify(typeVal)})` };
  }
  // ---- GATE 2: CPQ validated ----
  const validated = f(CONFIG.VALIDATED_FIELD).toLowerCase();
  if (validated !== "true" && validated !== "t") {
    return { ok: false, reason: `${CONFIG.VALIDATED_FIELD} is not checked` };
  }
  if (isDontUse(itemid)) {
    return { ok: false, reason: `part number is marked Don't Use / Duplicate` };
  }

  // ---- inputs: structured field first, part-number fallback when blank ----
  const nt = nameTokens(itemid) ?? { material: "", die: "", od: null as number | null };
  const dieCode = DIE[f(FIELDS.die)] ?? nt.die ?? "";
  const material = MATL[f(FIELDS.matl)] ?? nt.material ?? "";
  // OD_FIELD is empty in the shipped config, so the part number is the source. Falls
  // back to custitem_flamecellod, which carries the same number, when the name does
  // not parse.
  let od: number | null = CONFIG.OD_FIELD ? parseInches(f(CONFIG.OD_FIELD)) : null;
  if (od == null && nt.od != null) od = nt.od;
  if (od == null) od = parseInches(f(FIELDS.od));

  const cr = computeRolling(dieCode, material, od);
  if ("error" in cr) {
    return { ok: false, reason: `${cr.error} (die=${JSON.stringify(dieCode)}, material=${JSON.stringify(material)}, od=${od})` };
  }

  // Untagged plug counts as welded, matching the original's `plugId ? ... : true`.
  const plugId = f(FIELDS.plug);
  const welded = plugId ? WELDED_PLUGS[plugId] === true : true;
  const housingAdd = HOUSING_ADD[f(FIELDS.housing)] ?? 0;
  const inspRaw = INSP_MIN[f(FIELDS.insp)];
  const insp = inspRaw == null ? DEFAULT_INSP_MIN : inspRaw;
  const tig = (welded ? TIG_BASE : 0) + housingAdd;

  const operations: ComputedRoutingOp[] = [];
  operations.push(step(CONFIG.SEQ_ROLL, CONFIG.NAME_ROLL, cr.rollWC, CONFIG.CT_ROLL, cr.roll, true));
  if (tig > 0) {
    operations.push(step(CONFIG.SEQ_TIG, CONFIG.NAME_TIG, CONFIG.WC_TIG, CONFIG.CT_TIG, tig, false));
  }
  // Never first -- Cell Rolling is always pushed above -- so this always carries the
  // connection type, same as the original.
  operations.push(step(CONFIG.SEQ_SHIP, CONFIG.NAME_SHIP, CONFIG.WC_SHIP, CONFIG.CT_SHIP, insp, false));

  return {
    ok: true,
    operations,
    subsidiaryId: CONFIG.SUBSIDIARY_ID,
    locationId: CONFIG.LOCATION_ID,
    detail:
      `die ${dieCode} / ${material} / OD ${od} -> roll ${cr.roll} ` +
      `(${cr.rollWC === CONFIG.WC_ROLL_PA ? "PA" : "CC"}), tig ${tig}, insp ${insp}`,
  };
}
